import React, { useEffect, useState } from "react";
import { 
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  Spinner,
} from "reactstrap";
import {
  getMultipleOptDashboardLink,
  getMultipleDashboardLink,
  get6DofDashboardLink
} from "src/services/simulator-flight";
import { useTranslation } from "react-i18next";
import { SIMULATION_RESULT, DASHBOARD_TYPE, DASHBOARD_STATUS_BY_TYPE, DASHBOARD_URL_BY_TYPE, DASHBOARD_STATUS, MULTI_DASHBOARD_TYPE, RETRY_TIME } from "src/constant/common";
import TooltipWrapper from "../TooltipWrapper";
import { useQuery } from "src/Hooks/useReactQuery";
import { alertState } from "src/recoil/atoms/global";
import { useSetRecoilState } from "recoil";
import { SingleDashboard } from "src/components/Common/Button";
import { isNewSimulationVersion } from "src/utils/common";
import { REACT_APP_DIA_DIP_TAG_VERSION } from "src/constant/environment";

const SimulationMultiDashboard = ({simulation, isLoading, isShow = true, setDashboardLoading }) => {
  const { t } = useTranslation("flight-simulator");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isGetMultiDashboard, setIsGetMultiDashboard] = useState(false);
  const [isGetMultiOptDashboard, setIsGetMultiOptDashboard] = useState(false);
  const [isGetMultiDiaDashboard, setIsGetMultiDiaDashboard] = useState(false);
  const [isGetMultiDebrisImpactDashboard, setIsGetMultiDebrisImpactDashboard] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const showAlert = useSetRecoilState(alertState);

  const stopFetching = () => {
    setIsGetMultiDashboard(false);
    setIsGetMultiOptDashboard(false);
    setIsGetMultiDiaDashboard(false);
    setIsGetMultiDebrisImpactDashboard(false);
    setRetryCount(0);
  }

  const handleClickMultipleDashboard = (type) => {
    let status = simulation[DASHBOARD_STATUS_BY_TYPE[type]];
    let url = simulation[DASHBOARD_URL_BY_TYPE[type]];
    if (type === MULTI_DASHBOARD_TYPE.FRAGMENT) {
      status = getDashboardStatus(simulation, DASHBOARD_TYPE.FRAGMENT_ANALYSIS);
      url = getDashboardUrl(DASHBOARD_TYPE.FRAGMENT_ANALYSIS);
    }
    if (type === MULTI_DASHBOARD_TYPE.DEBRIS_IMPACT) {
      status = getDashboardStatus(simulation, DASHBOARD_TYPE.DEBRIS_IMPACT);
      url = getDashboardUrl(DASHBOARD_TYPE.DEBRIS_IMPACT);
    }
    if (status === DASHBOARD_STATUS.SUCCEEDED && url) {
      window.open(url, "_blank");
      return;
    }
    if (type === MULTI_DASHBOARD_TYPE.MULTI) {
      setIsGetMultiDashboard(true);
      return;
    }
    if (type === MULTI_DASHBOARD_TYPE.FRAGMENT) {
      setIsGetMultiDiaDashboard(true);
      return;
    }
    if (type === MULTI_DASHBOARD_TYPE.DEBRIS_IMPACT) {
      setIsGetMultiDebrisImpactDashboard(true);
      return;
    }
    setIsGetMultiOptDashboard(true);
  };

  const handleGetDashboardSuccess = (data, type) => {
    const status = data?.data?.status || data?.data?.dashboard_status;
    const url = data?.data?.url || data?.data?.dashboard_url;
    if (status === DASHBOARD_STATUS.SUCCEEDED && url) {
      stopFetching();
      window.open(url, "_blank");
      return;
    }
    if (status === DASHBOARD_STATUS.INPROGRESS) {
      setRetryCount((prevCount) => prevCount + 1);
      if (retryCount >= RETRY_TIME) {
        showAlert({
          status: true,
          message: t("over_time"),
          type: "danger",
        });
        stopFetching();
        return;
      }
    }
  };

  const handleGetDashboardError = (error) => {
    const { error: errorData, message } = error?.response?.data ?? {};
    showAlert({
      status: true,
      message: errorData?.message || message,
      type: "danger",
    });
    stopFetching();
  };

  const getDashboardUrl = (dashboardType) => {
    const dashboard = simulation?.simulator_dashboards.find(
      d => d.dashboard_type === dashboardType
    );
    return dashboard ? dashboard.dashboard_url : null;
  }

  const getDashboardStatus = (simulation, dashboardType) => {
    const dashboard = simulation?.simulator_dashboards.find(
      d => d.dashboard_type === dashboardType
    );
    return dashboard ? dashboard.dashboard_status : null;
  }

  const { isFetching: isLoadingMulti } = useQuery(
    [`getMultipleDashboard_${simulation?.id}`],
    () => getMultipleDashboardLink({ id: simulation.id }),
    {
      enabled: isGetMultiDashboard && !simulation.multi_dashboard_url,
      refetchInterval: 3000,
      onSuccess: (data) => handleGetDashboardSuccess(data, MULTI_DASHBOARD_TYPE.MULTI),
      onError: (error) => handleGetDashboardError(error),
    }
  );

  const { isFetching: isLoadingMultiOpt } = useQuery(
    [`getMultipleOptDashboard_${simulation?.id}`],
    () => getMultipleOptDashboardLink({ id: simulation.id }),
    {
      enabled: isGetMultiOptDashboard && !simulation.opt_dashboard_url,
      refetchInterval: 3000,
      onSuccess: (data) => handleGetDashboardSuccess(data, MULTI_DASHBOARD_TYPE.OPT),
      onError: (error) => handleGetDashboardError(error),
    }
  );

  const { isFetching: isLoadingDia } = useQuery(
    [`getMultipleDiaDashboard_${simulation?.id}`],
    () =>
      get6DofDashboardLink({
        id: simulation.id,
        dashboard_input_type: 'multiple',
        dashboard_type: DASHBOARD_TYPE.FRAGMENT_ANALYSIS
      }),
    {
      enabled: isGetMultiDiaDashboard && !getDashboardUrl(DASHBOARD_TYPE.FRAGMENT_ANALYSIS),
      refetchInterval: 3000,
      onSuccess: (data) => handleGetDashboardSuccess(data, MULTI_DASHBOARD_TYPE.FRAGMENT),
      onError: (error) => handleGetDashboardError(error),
    }
  );

  const { isFetching: isLoadingDebrisImpact } = useQuery(
    [`getMultipleDebrisImpactDashboard_${simulation?.id}`],
    () =>
      get6DofDashboardLink({
        id: simulation.id,
        dashboard_input_type: 'multiple',
        dashboard_type: DASHBOARD_TYPE.DEBRIS_IMPACT
      }),
    {
      enabled: isGetMultiDebrisImpactDashboard && !getDashboardUrl(DASHBOARD_TYPE.DEBRIS_IMPACT),
      refetchInterval: 3000,
      onSuccess: (data) => handleGetDashboardSuccess(data, MULTI_DASHBOARD_TYPE.DEBRIS_IMPACT),
      onError: (error) => handleGetDashboardError(error),
    }
  );

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  const isMainDashboard = simulation.execution_file_name === SIMULATION_RESULT.SINGLE;
  const isMultiParamsDashboard = simulation.execution_file_name === SIMULATION_RESULT.MULTI;
  const isMultiOptDashboard = simulation.execution_file_name === SIMULATION_RESULT.PAYLOAD || simulation.execution_file_name === SIMULATION_RESULT.PAYLOAD_SOE_ASSIST;
  const isDebrisImpactDashboard = simulation.execution_file_name === SIMULATION_RESULT.DEBRIS_IMPACT_ANALYSIS;

  const isDashboardReady = () => {
    const isFragmentReady = simulation.is_fragment_analysis ? getDashboardUrl(DASHBOARD_TYPE.FRAGMENT_ANALYSIS) : true;
    if (isMultiOptDashboard || isMultiParamsDashboard) {
      return simulation.opt_dashboard_url && simulation.multi_dashboard_url && isFragmentReady;
    }
    return simulation.multi_dashboard_url && isFragmentReady;
  }

  const renderDropdownItemLabel = (type) => {
    return isMultiOptDashboard ? t(`list.table.payload_${type}_dashboard`) : t(`list.table.${type}_dashboard`);
  };
  
  useEffect(() => {
    if (isLoadingMulti || isLoadingMultiOpt || isLoadingDia || isLoadingDebrisImpact || retryCount > 0) {
      setDashboardLoading((oldVal) => ({ ...oldVal, [simulation.id]: true }));
    }
    if (!isLoadingMulti && !isLoadingMultiOpt && !isLoadingDia && !isLoadingDebrisImpact && retryCount === 0) {
      setDashboardLoading((oldVal) => ({ ...oldVal, [simulation.id]: false }));
    }
  }, [isLoadingMulti, isLoadingMultiOpt, isLoadingDia, isLoadingDebrisImpact, retryCount]);

  if (!isShow) {
    return null;
  }

  if (isLoading) {
    return <Spinner size="sm" color="secondary" />;
  }

  if (isDebrisImpactDashboard) {
    return (
      <SingleDashboard
        id={`${simulation.id}-debris-impact`}
        isLoading={isLoading}
        isReady={getDashboardUrl(DASHBOARD_TYPE.DEBRIS_IMPACT)}
        handleClick={() => handleClickMultipleDashboard(MULTI_DASHBOARD_TYPE.DEBRIS_IMPACT)}
      />
    )
  }

  if (isMainDashboard && (
    !simulation.is_fragment_analysis ||
    (simulation.is_fragment_analysis && !isNewSimulationVersion(simulation.version_name, REACT_APP_DIA_DIP_TAG_VERSION))
  )) {
    return (
      <SingleDashboard
        id={`${simulation.id}-main`}
        isLoading={isLoading}
        isReady={simulation.multi_dashboard_url}
        handleClick={() => handleClickMultipleDashboard(MULTI_DASHBOARD_TYPE.MULTI)}
      />
    )
  }
  
  return (
   
    <TooltipWrapper
      id={`dashboard-${simulation?.id}`}
      tooltipText={t("list.table.dashboard")}
    >
      <Dropdown
        isOpen={dropdownOpen}
        toggle={toggleDropdown}
        className="align-self-center"
        data-tooltip-id={`dashboard-${simulation?.id}`}
        data-tooltip-content={t("list.table.dashboard")}
      >
        <DropdownToggle
          data-toggle="dropdown"
          tag="button"
          className="border-0 bg-transparent"
        >
          <i className={`bi bi-speedometer2 ${isDashboardReady() ? 'text-success' : 'text-dark'} f-s-20`}/>
        </DropdownToggle>
        
        <DropdownMenu
          end={true}
          className="topbar-dropdown-menu profile-dropdown-items"
        >
          <div onClick={toggleDropdown}>
            <div
              role="button"
              className={`dropdown-item ${simulation.multi_dashboard_url ? 'text-success' : 'text-dark'}`}
              onClick={() => handleClickMultipleDashboard(MULTI_DASHBOARD_TYPE.MULTI)}
            >
              <span>{renderDropdownItemLabel('multi_param')}</span>
            </div>
            {
              simulation.is_fragment_analysis && isNewSimulationVersion(simulation.version_name, REACT_APP_DIA_DIP_TAG_VERSION) && (
                <div
                  role="button"
                  className={`dropdown-item ${getDashboardUrl(DASHBOARD_TYPE.FRAGMENT_ANALYSIS) ? 'text-success' : 'text-dark'}`}
                  onClick={() => handleClickMultipleDashboard(MULTI_DASHBOARD_TYPE.FRAGMENT)}
                >
                  <span>{t('list.table.dia_dashboard')}</span>
                </div>
              )
            }
            {
              (isMultiOptDashboard || isMultiParamsDashboard) && (
                <div
                  role="button"
                  className={`dropdown-item ${simulation.opt_dashboard_url ? 'text-success' : 'text-dark'}`}
                  onClick={() => handleClickMultipleDashboard(MULTI_DASHBOARD_TYPE.OPT)}
                >
                  <span>{renderDropdownItemLabel('multi_opt')}</span>
                </div>
              )
            }
          </div>
        </DropdownMenu>
      </Dropdown>
    </TooltipWrapper>
  );
};

export default SimulationMultiDashboard;
